<template>
  <MiFormItem
    :class="[
      'form-search-select',
      {
        'form-search-select_in-column': props.inColumn,
        'form-search-select_in-line-space-between':
          !props.inColumn && !!props.label && props.spaceBetween,
        'form-search-select-custom': customLabel,
      },
    ]"
    :error="errorMessage"
    :label="props.label"
    :required="props.required">
    <template v-if="customLabel" #label>
      <span class="form-search-select_label">{{ props.label }}</span>
      <MiButton
        class="form-search-select_btn"
        type="primary"
        text
        link
        @click="$emit('openModal', $event)">
        {{ customLabel }}
      </MiButton>
    </template>
    <MiSelect
      class="form-search-select__select"
      v-model="value"
      remote
      filterable
      reserve-keyword
      :validate-event="false"
      :remote-method="debouncedGetItems"
      :loading="loading"
      :multiple="multiple"
      :disabled="props.disabled"
      :placeholder="props.placeholder || $t('Base.PleaseInput')"
      :clearable="props.clearable"
      :no-data-text="$t('Base.NoData')"
      :collapse-tags="props.collapseTags"
      :collapse-tags-tooltip="props.collapseTagsTooltip"
      @update:model-value="$emit('update:modelValue', $event)"
      @visible-change="getItems"
      @change="selectHandler"
      @clear="handleClear"
      popper-class="form-search-select__popper"
      ref="component">
      <template #prefix>
        <MiIcon icon="SEARCH" class="form-search-select__icon" />
      </template>

      <ElOption
        class="form-search-select__option"
        v-for="item in items"
        :key="item[props.valueKey]"
        :label="item[props.labelKey]"
        :value="item[props.valueKey]">
        <slot name="optionTemplate" :item="item" />
      </ElOption>

      <template #empty>
        <div class="form-search-select-empty">
          <div class="form-search-select-empty__text">
            {{ loading ? 'Loading' : $t('Base.NoData') }}
          </div>

          <MiButton
            v-if="props.showCreateOption"
            v-show="!loading"
            class="form-search-select-empty__create"
            type="primary"
            size="default"
            @click="createItem">
            <template #icon>
              <MiIcon icon="PLUS" />
            </template>
            {{ $t('Base.Create') }}
          </MiButton>
        </div>
      </template>
    </MiSelect>
  </MiFormItem>
</template>

<script lang="ts" setup>
import { ElSelect } from 'element-plus';
import cloneDeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';
import { useField } from 'vee-validate';
import { computed, ref, watch } from 'vue';

import { AnyObject } from '~shared/types';
import { MiIcon, MiSelect, MiButton, MiFormItem } from '~shared/ui';

type Props = {
  inColumn?: boolean;
  spaceBetween?: boolean;
  clearable?: boolean;
  collapseTags?: boolean;
  collapseTagsTooltip?: boolean;
  showCreateOption?: boolean;
  disabled?: boolean;
  required?: boolean;
  fieldObjectName: string;
  multiple?: boolean;
  fieldName: string;
  label?: string;
  labelKey?: string;
  valueKey?: string;
  placeholder?: string;
  methodName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetchData: (query?: any) => Promise<{ data?: any } | undefined>;
  customLabel?: string;
  searchQuery?: object;
};

const props = withDefaults(defineProps<Props>(), {
  inColumn: true,
  spaceBetween: false,
  clearable: false,
  collapseTags: false,
  collapseTagsTooltip: false,
  showCreateOption: false,
  disabled: false,
  required: false,
  multiple: false,
  labelKey: 'title',
  valueKey: 'id',
  label: '',
  placeholder: '',
  methodName: 'find',
  customLabel: '',
  query: () => ({}),
});

const emit = defineEmits(['select', 'create', 'update:modelValue', 'openModal', 'clear']);

const loading = ref(false);
const items = ref<AnyObject[]>([]);

// eslint-disable-next-line vue/no-setup-props-destructure
const { value, setValue, errorMessage } = useField<
  string | string[] | number | number[] | undefined
>(props.fieldName);

// eslint-disable-next-line vue/no-setup-props-destructure
const { value: objectValue, setValue: setObjectValue } = useField<
  AnyObject | AnyObject[] | undefined
>(props.fieldObjectName);

const component = ref<InstanceType<typeof ElSelect> | null>(null);

const createItem = () => {
  if (component.value) {
    const query = component.value.query;
    component.value.query = '';
    return emit('create', query);
  }
};

// eslint-disable-next-line vue/no-dupe-keys
const searchQuery = computed(() => component.value?.query);

const getItems = async () => {
  loading.value = true;

  const response = await props.fetchData({
    ...props.query,
    page: 1,
    per_page: 30,
    search: searchQuery.value,
  });

  if (objectValue.value && response?.data) {
    const arrayOfvalue = (
      Array.isArray(objectValue.value) ? objectValue.value : [objectValue.value]
    ).filter((f) => !!f.id);
    const ids = arrayOfvalue.map((item) => item.id);
    const optionList = Array.isArray(response.data) ? response.data : response.data?.data;
    const newItems = optionList.filter((f: AnyObject) => {
      return !ids.includes(f.id);
    });
    items.value = [...arrayOfvalue, ...newItems];
  } else {
    items.value = Array.isArray(response?.data) ? response?.data : response?.data?.data ?? [];
  }

  loading.value = false;
};

const debouncedGetItems = debounce(getItems, 500);

const selectHandler = (eventValue: string | number | string[] | number[]) => {
  setValue(eventValue);

  let result: AnyObject | AnyObject[] | undefined;

  if (Array.isArray(eventValue)) {
    result = eventValue.map((item) => {
      return items.value.find((f) => f[props.valueKey] === item);
    });
  } else {
    result = items.value.find((f) => f[props.valueKey] === eventValue);
  }
  const cloneResult = cloneDeep(result);

  if (cloneResult) {
    setObjectValue(cloneResult);
    emit('select', cloneResult);
  }
};

const handleClear = () => {
  emit('clear');
};

watch(
  () => value.value,
  (v) => {
    if (!v) {
      setObjectValue(undefined);
    }
  }
);

watch(
  () => objectValue.value,
  (newValue) => {
    if (newValue) {
      const arrayOfvalue = (Array.isArray(newValue) ? newValue : [newValue]).filter(
        (f) => !!f?.[`${props.valueKey}`]
      );
      const ids = arrayOfvalue.map((item) => item[props.valueKey]);
      const newItems = items.value.filter((f) => {
        return !ids.includes(f[`${props.valueKey}`]);
      });

      items.value = [...arrayOfvalue, ...newItems];
    } else {
      items.value = [];
    }
  },
  { immediate: true, deep: true }
);
</script>

<style lang="scss" src="./index.scss" />
<i18n src="@/locales/base.locales.json" />
